import { toAmericanDate, toBrazilianDate } from '../../../../../helpers/utils/utils';

export const TIPO_FORMA_PAGTO_CARTAO = 2;
export const TIPO_FORMA_PAGTO_DINHEIRO = 5;
export const TIPO_FORMA_PAGTO_CHEQUE = 6;

export const COD_RESULT_SUCESSO = 0;
export const COD_RESULT_AVISO_OU_ERRO_SOMATORIA = 1;
export const COD_RESULT_AVISO = 2;
export const COD_RESULT_ERRO_SOMATORIA_ALCADA = 1301;
export const COD_RESULT_ERRO_SOMATORIA_LIMITE = 1305;
export const COD_RESULT_ERRO_SOMATORIA_GENERICO = -1;

export const MENSAGEM_ERRO_SOMATORIA = 'O valor definido para o Acordo difere da soma das parcelas. Revise os valores e tente novamente.';
export const MENSAGEM_PROPOSTA_SUCESSO = 'Proposta gerada com sucesso. Foi enviado um E-mail para o aprovador.';
export const MENSAGEM_ERRO_GERAR = 'Não foi possível concluir a operação. Tente novamente.';

/**
 * Lógica pura do Passo 3 — port de `gerarAcordoController.js`/lógica de
 * redistribuição de `DialogNovaAlterarParcela.jsx` reais, sem `FieldModel`.
 *
 * Datas de formulário (inputs `type="date"`) trafegam em ISO; `parcela.vencimento`
 * (array de parcelas, já estabelecido desde a Etapa 5/`buildParcelas`) continua
 * em formato brasileiro — `toAmericanDate`/`toBrazilianDate` (utilitários puros
 * já reaproveitados nas etapas anteriores) fazem a conversão nos dois sentidos.
 */

function apenasDigitos(valor) {
  return (valor || '').replace(/\D/g, '');
}

/** Máscara de telefone dinâmica por prefixo — mesma regra de `NovoAcordoPassoTres.jsx` real. */
export function aplicarMascaraTelefone(valor) {
  const digitos = apenasDigitos(valor);
  const primeiro = digitos[0];

  if (primeiro === '0') {
    return digitos.slice(0, 11).replace(/(\d{4})(\d{0,3})(\d{0,4})/, (match, a, b, c) => [a, b, c].filter(Boolean).join('-'));
  }
  if (['1', '2', '3', '4', '5'].includes(primeiro)) {
    return digitos.slice(0, 8).replace(/(\d{4})(\d{0,4})/, (match, a, b) => [a, b].filter(Boolean).join('-'));
  }
  if (['6', '7', '8', '9'].includes(primeiro)) {
    return digitos.slice(0, 9).replace(/(\d{5})(\d{0,4})/, (match, a, b) => [a, b].filter(Boolean).join('-'));
  }
  return digitos;
}

export function contarPalavras(texto) {
  return texto.trim().split(/\s+/).filter(Boolean).length;
}

export function calcularDataMinima({ nParcela, dataPrimeiroVencimentoISO }) {
  const data = new Date(dataPrimeiroVencimentoISO);
  if (nParcela !== 1) {
    data.setDate(data.getDate() + 1);
  }
  return data;
}

export function calcularDataMaxima({
  nParcela, dataPrimeiroVencimentoISO, dataFinalISO, prazoMaximoPrimeiroVencimento,
}) {
  if (nParcela === 1) {
    const data = new Date(dataPrimeiroVencimentoISO);
    data.setDate(data.getDate() + (prazoMaximoPrimeiroVencimento || 0));
    return data;
  }
  return new Date(dataFinalISO);
}

/**
 * Redistribui o valor restante (total do acordo - soma das parcelas
 * anteriores - valor da parcela editada) entre as parcelas posteriores à
 * editada, igualmente, com a última parcela absorvendo o resto de
 * arredondamento — mesma regra de `redistribuirParcelas` real, mas pura (não
 * muta as parcelas recebidas).
 */
export function redistribuirValores({
  parcelas, parcelaEditada, valorTotalAcordo, quantidadeParcelasFormulario,
}) {
  const indiceEditado = parcelaEditada.nParcela - 1;
  const somaAnteriores = parcelas
    .slice(0, indiceEditado)
    .reduce((total, parcela) => total + parcela.valor, 0);

  let valorRestante = parseFloat(valorTotalAcordo.toFixed(2))
    - parseFloat(somaAnteriores.toFixed(2))
    - parseFloat(parcelaEditada.valor);

  const parcelasRestantes = quantidadeParcelasFormulario - parcelaEditada.nParcela;
  const valorIndividual = parcelasRestantes > 0 ? parseFloat(valorRestante.toFixed(2)) / parcelasRestantes : 0;

  return parcelas.map((parcelaAtual, indice) => {
    if (indice === indiceEditado) {
      return {
        ...parcelaAtual,
        valor: Number(parseFloat(parcelaEditada.valor).toFixed(2)),
        formaPagamento: parcelaEditada.formaPagamento,
        redistribuirValores: parcelaEditada.redistribuirValores,
        vencimento: parcelaEditada.vencimento,
        chequeDetalhe: parcelaEditada.chequeDetalhe,
      };
    }
    if (indice > indiceEditado && indice !== parcelas.length - 1) {
      const valor = Math.max(Number(valorIndividual.toFixed(2)), 0);
      valorRestante -= valor;
      return { ...parcelaAtual, valor };
    }
    if (indice === parcelas.length - 1 && indice > indiceEditado) {
      return { ...parcelaAtual, valor: Math.max(Number(valorRestante.toFixed(2)), 0) };
    }
    return parcelaAtual;
  });
}

/**
 * Restrições de troca de forma de pagamento ao editar uma parcela — port
 * fiel de `getFormasPagamento` (`DialogNovaAlterarParcela.jsx` real): uma
 * parcela em Cartão só pode continuar em Cartão; uma parcela em Dinheiro só
 * pode virar outra coisa se o acordo inteiro (Passo 2) não foi todo em
 * Dinheiro; Cartão nunca aparece como opção para quem já não estava em Cartão.
 */
export function opcoesFormaPagamentoParaParcela(formaPagamentoAtual, formaPagamentoPasso2, todasOpcoes) {
  switch (formaPagamentoAtual) {
    case 0:
    case 1:
    case 6:
      return todasOpcoes.filter(opcao => opcao.id !== TIPO_FORMA_PAGTO_CARTAO);
    case TIPO_FORMA_PAGTO_CARTAO:
      return todasOpcoes.filter(opcao => opcao.id === TIPO_FORMA_PAGTO_CARTAO);
    case TIPO_FORMA_PAGTO_DINHEIRO:
      if (formaPagamentoPasso2 === TIPO_FORMA_PAGTO_DINHEIRO) {
        return todasOpcoes.filter(opcao => opcao.id === TIPO_FORMA_PAGTO_DINHEIRO);
      }
      return todasOpcoes.filter(opcao => opcao.id !== TIPO_FORMA_PAGTO_CARTAO);
    default:
      return todasOpcoes;
  }
}

function descricaoFormaPagamento(id, formasPagamentoOptions) {
  const opcao = formasPagamentoOptions.find(item => item.id === id);
  return opcao ? opcao.value : '';
}

/** Payload de `gerarAcordoPasso3` — port fiel de `gerarAcordoController.makePayload`. */
export function makePayloadGerarAcordo({
  idProcesso, dadosResumo, parcelas, dadosPadraoForm, tipo, formasPagamentoOptions,
  idUsuarioAprovador, justificativa, telefone,
}) {
  return {
    idProcesso,
    numero: telefone.numero ? telefone.numero.replace('-', '') : telefone.numero,
    ddd: telefone.ddd,
    tipoTelefone: telefone.tipoTelefone,
    porcentagemTaxaJuros: dadosPadraoForm.porcentagemJuros || 0,
    porcentagemHonorarios: dadosPadraoForm.porcentagemHonorarios || 0,
    porcentagemDesconto: dadosPadraoForm.porcentagemDesconto || 0,
    titulosJson: dadosResumo.jsonTitulos,
    erros: dadosResumo.jsonErros,
    tipoAcordo: tipo,
    qtdeParcelasUsuario: dadosPadraoForm.quantidadeParcelas,
    idUsuarioPermiteSimulacao: idUsuarioAprovador || 0,
    justificativa,
    porcentagemTaxaCartao: dadosResumo.porcentagemTaxaCartao,
    valorTaxaCartao: dadosResumo.valorTaxaCartao,
    parcelas: parcelas.map(parcela => ({
      nroParcela: parcela.nParcela,
      valor: parcela.valor,
      dataVencimento: toAmericanDate(parcela.vencimento),
      idTipoFormaPagto: parcela.formaPagamento,
      descricaoFormaPagtoAcordo: descricaoFormaPagamento(parcela.formaPagamento, formasPagamentoOptions),
      redistribuirValores: Boolean(parcela.redistribuirValores),
      chequeDetalhe: (parcela.chequeDetalhe || []).map(detalhe => ({
        idBanco: detalhe?.idBanco?.value ?? 0,
        serChequeTerceiro: detalhe?.serChequeTerceiro?.value ? 'S' : 'N',
        numeroAgencia: detalhe?.numeroAgencia?.value ?? '',
        numeroConta: detalhe?.numeroConta?.value ?? '',
        dac: detalhe?.dac?.value ?? '',
        numeroCheque: detalhe?.numeroCheque?.value ?? '',
        valor: detalhe?.valor?.value ?? '',
      })),
    })),
  };
}

const MENSAGENS_SUCESSO_POR_TIPO = {
  A: 'Acordo gerado com sucesso.',
  S: 'Simulação gerada com sucesso.',
  SP: MENSAGEM_PROPOSTA_SUCESSO,
};

/**
 * Decide a ação a tomar a partir do `codResult`/`msgResult` de `gerarAcordoPasso3`
 * — função pura extraída de `submeterGeracao` (`NovoAcordoPassoTres.jsx`) para
 * isolar a lógica de decisão, testável sem side-effects. Achado em code
 * review: o `case` de código 1 misturava número + comparação exata de texto
 * do backend (frágil a mudança de wording), e o chamador original disparava
 * `setAviso` seguido de `onFechar()` no mesmo tick — como `onFechar` desmonta
 * a árvore, o Snackbar de aviso nunca chegava a ser visto. Por isso o retorno
 * usa uma ação `AVISO_E_FECHAR` distinta de `FECHAR`: o chamador só fecha
 * quando o próprio Snackbar de aviso for dispensado, não antes.
 */
export function resolverResultadoGeracao(data, tipo) {
  switch (data.codResult) {
    case COD_RESULT_SUCESSO:
      return { acao: 'FECHAR' };
    case COD_RESULT_AVISO_OU_ERRO_SOMATORIA:
      if (data.msgResult === MENSAGEM_ERRO_SOMATORIA) {
        return { acao: 'ERRO_SOMATORIA', mensagem: data.msgResult };
      }
      if (data.msgResult === 'OK' || data.msgResult === MENSAGENS_SUCESSO_POR_TIPO[tipo]) {
        return { acao: 'FECHAR' };
      }
      return { acao: 'AVISO_E_FECHAR', mensagem: data.msgResult };
    case COD_RESULT_AVISO:
      return { acao: 'AVISO_E_FECHAR', mensagem: data.msgResult };
    case COD_RESULT_ERRO_SOMATORIA_ALCADA:
    case COD_RESULT_ERRO_SOMATORIA_LIMITE:
    case COD_RESULT_ERRO_SOMATORIA_GENERICO:
      return { acao: 'ERRO_SOMATORIA', mensagem: data.msgResult };
    default:
      return { acao: 'ERRO', mensagem: data.msgResult || MENSAGEM_ERRO_GERAR };
  }
}

export function vencimentoParaIso(vencimentoBr) {
  return toAmericanDate(vencimentoBr);
}

export function isoParaVencimento(dataIso) {
  return toBrazilianDate(dataIso);
}
